import {
  completedExerciseIndices,
  learningLessons,
  openingLessonManifest,
} from "@chesshotel/types";
import { LearningOverview } from "./learningTypes";

// These are completion milestones, not an estimate of playing strength.
export const levelThresholds = [0, 1, 3, 6, 10, 15, 21, 27, 33, 37];

// The starting level has no earned medal. Each subsequent milestone upgrades
// the same artwork family used by the game and puzzle leagues.
export const learningMedals = [
  "bronze100_square.svg",
  "bronze50_square.svg",
  "bronze25_square.svg",
  "bronze8_square.svg",
  "silver8_square.svg",
  "gold8_square.svg",
  "platinum8_square.svg",
  "diamond8_square.svg",
  "master8_v2_square.svg",
];

export function linesComplete(
  completed: unknown,
  moves: unknown,
  totals: number[]
) {
  const indices = completedExerciseIndices(completed, totals.length);
  let progress: unknown = moves;
  try {
    if (typeof moves === "string") progress = JSON.parse(moves);
  } catch {
    progress = [];
  }
  return totals.every(
    (total, i) =>
      indices.includes(i) ||
      (Array.isArray(progress) &&
        Number.isSafeInteger(progress[i]) &&
        progress[i] >= total)
  );
}

export function learningProgress(data: LearningOverview) {
  const subjects = (["basics", "tactics", "strategy", "endgames"] as const).map(
    (key) => {
      const lessons = learningLessons.filter((lesson) => lesson.group === key);
      return {
        key,
        total: lessons.length,
        completed: lessons.filter(
          (lesson) =>
            completedExerciseIndices(
              data.lessonProgress?.find((row) => row.slug === lesson.slug)
                ?.completedExercises,
              lesson.exercises.length
            ).length === lesson.exercises.length
        ).length,
      };
    }
  );
  const openings = Object.entries(openingLessonManifest);
  const completedOpenings = openings.filter(([slug, course]) => {
    const row = data.openingProgress?.find((item) => item.slug === slug);
    return (
      linesComplete(
        row?.completedLines,
        row?.practiceLineProgress,
        course.practice
      ) &&
      (row?.challengeCompleted === true ||
        linesComplete(
          row?.completedChallengeLines,
          row?.challengeLineProgress,
          course.challenge
        ))
    );
  }).length;
  const allSubjects = [
    { key: "openings", total: openings.length, completed: completedOpenings },
    ...subjects,
  ];
  const completed = allSubjects.reduce(
    (sum, subject) => sum + subject.completed,
    0
  );
  const level = levelThresholds.filter(
    (threshold) => completed >= threshold
  ).length;
  return {
    subjects: allSubjects,
    completed,
    level,
    medalSrc:
      level > 1 ? `/images/medals/${learningMedals[level - 2]}` : undefined,
    nextMedalSrc: learningMedals[level - 1]
      ? `/images/medals/${learningMedals[level - 1]}`
      : undefined,
    nextTarget: levelThresholds[level],
  };
}
